import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  ActivityCard,
  iconForType,
  toneForType,
  type ActivityLogEntry,
} from '@/components/agent/WorkingView';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

interface CampaignStats {
  contactsAdded: number;
  emailsSent: number;
  replies: number;
  bounces: number;
  opportunities: number;
  chartData: Array<{
    day: string;
    date: string;
    leads: number;
    emails: number;
    bounces: number;
    replies: number;
    opportunities: number;
  }>;
}

export function LiveFeedTab({ agentId }: { agentId?: string }) {
  const { id: campaignId } = useParams<{ id: string }>();
  const { data: stats } = useSWR<CampaignStats>(
    campaignId ? `/api/campaigns/${campaignId}/stats` : null,
    fetcher,
    { refreshInterval: 30000 }
  );

  const { data: activity, error: activityError } = useSWR<ActivityLogEntry[]>(
    agentId ? `/api/agents/${agentId}/activity` : null,
    fetcher,
    { refreshInterval: 5000 }
  );
  const entries = Array.isArray(activity) ? activity : [];

  const chartData = stats?.chartData ?? [];

  return (
    <div className="space-y-gutter-lg">
      {/* Metric cards */}
      <div className="grid grid-cols-5 gap-gutter-lg">
        <div className="bg-tint-rose rounded-inner p-5">
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-tint-rose-foreground">Leads Added</p>
            <Tooltip delayDuration={100}>
              <TooltipTrigger><InformationCircleIcon className="h-3.5 w-3.5 text-tint-rose-foreground" /></TooltipTrigger>
              <TooltipContent side="bottom">Leads added to this campaign</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-semibold mt-2 text-tint-rose-foreground">{stats?.contactsAdded ?? 0}</p>
        </div>
        <div className="bg-tint-green rounded-inner p-5">
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-tint-green-foreground">Emails Sent</p>
            <Tooltip delayDuration={100}>
              <TooltipTrigger><InformationCircleIcon className="h-3.5 w-3.5 text-tint-green-foreground" /></TooltipTrigger>
              <TooltipContent side="bottom">Total personalised emails sent</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-semibold mt-2 text-tint-green-foreground">{stats?.emailsSent ?? 0}</p>
        </div>
        <div className="bg-tint-amber rounded-inner p-5">
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-tint-amber-foreground">Bounces</p>
            <Tooltip delayDuration={100}>
              <TooltipTrigger><InformationCircleIcon className="h-3.5 w-3.5 text-tint-amber-foreground" /></TooltipTrigger>
              <TooltipContent side="bottom">Leads whose email bounced back as undeliverable</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-semibold mt-2 text-tint-amber-foreground">{stats?.bounces ?? 0}</p>
        </div>
        <div className="bg-tint-teal rounded-inner p-5">
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-tint-teal-foreground">Replies</p>
            <Tooltip delayDuration={100}>
              <TooltipTrigger><InformationCircleIcon className="h-3.5 w-3.5 text-tint-teal-foreground" /></TooltipTrigger>
              <TooltipContent side="bottom">Responses received from prospects</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-semibold mt-2 text-tint-teal-foreground">{stats?.replies ?? 0}</p>
        </div>
        <div className="bg-tint-purple rounded-inner p-5">
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-tint-purple-foreground">Opportunities</p>
            <Tooltip delayDuration={100}>
              <TooltipTrigger><InformationCircleIcon className="h-3.5 w-3.5 text-tint-purple-foreground" /></TooltipTrigger>
              <TooltipContent side="bottom">Conversations that led to a positive outcome</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-semibold mt-2 text-tint-purple-foreground">{stats?.opportunities ?? 0}</p>
        </div>
      </div>

      {/* Performance chart */}
      <div className="bg-card border border-divider rounded-inner p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-medium text-content-strong">Campaign Performance</h3>
            <p className="text-xs text-muted-foreground">Last 7 days</p>
          </div>
          <div />
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: 'var(--chart-tick)' }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 'auto']} tick={{ fontSize: 12, fill: 'var(--chart-tick)' }} axisLine={false} tickLine={false} width={30} />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'var(--popover)',
                  border: '1px solid var(--line-chart)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  padding: '10px 14px',
                  fontSize: '13px',
                }}
                cursor={{ stroke: 'var(--line-chart)' }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                formatter={(value: string) => <span style={{ marginRight: '1.33rem', color: 'var(--chart-legend)' }}>{value}</span>}
              />
              <Line type="monotone" dataKey="leads" stroke="var(--series-rose)" strokeWidth={2} dot={false} isAnimationActive={false} name="Leads Added" />
              <Line type="monotone" dataKey="emails" stroke="var(--series-green)" strokeWidth={2} dot={false} isAnimationActive={false} name="Emails Sent" />
              <Line type="monotone" dataKey="bounces" stroke="var(--series-amber)" strokeWidth={2} dot={false} isAnimationActive={false} name="Bounces" />
              <Line type="monotone" dataKey="replies" stroke="var(--series-teal)" strokeWidth={2} dot={false} isAnimationActive={false} name="Replies" />
              <Line type="monotone" dataKey="opportunities" stroke="var(--series-purple)" strokeWidth={2} dot={false} isAnimationActive={false} name="Opportunities" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Agent activity feed */}
      <div className="bg-card border border-divider rounded-inner p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-medium text-content-strong">Agent Activity</h3>
            <p className="text-xs text-muted-foreground">
              {agentId ? 'Live updates from this campaign’s agent' : 'No agent linked to this campaign'}
            </p>
          </div>
        </div>
        {agentId && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {activityError && (
              <p className="text-xs text-danger-foreground">Couldn't load activity feed.</p>
            )}
            {!activityError && entries.length === 0 && (
              <p className="text-xs text-muted-foreground">No activity yet. Run Auto Prospect or start the agent to populate this feed.</p>
            )}
            {entries.map(entry => (
              <ActivityCard
                key={entry.id}
                tone={toneForType(entry.type)}
                icon={iconForType(entry.type)}
                message={entry.message}
                timestamp={entry.createdAt}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
